import { useState, useCallback, useEffect, useRef } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { ChatMessage, Conversation } from "../../api/models";
import { getFrontEndSettings, historyList } from "../../api";
import { HISTORY_OFFSET_INCREMENT } from "./types";
import { checkAndHandleAuth } from "../../services/authService";

export const useChatHistory = () => {
  const [showHistoryBtn, setShowHistoryBtn] = useState(true);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [fetchingChatHistory, setFetchingChatHistory] = useState(false);
  const [fetchingConvMessages, setFetchingConvMessages] = useState(false);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [selectedConvId, setSelectedConvId] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);
  const [hasMoreRecords, setHasMoreRecords] = useState<boolean>(true);
  const [clearing, setClearing] = useState(false);
  const [clearingError, setClearingError] = useState(false);
  const [toggleSpinner, setToggleSpinner] = useState(false);
  const [hideClearAllDialog, { toggle: toggleClearAllDialog }] =
    useBoolean(true);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current && import.meta.env.MODE === "development") {
      firstRender.current = false;
      return;
    }
    (async () => {
      const response = await getFrontEndSettings();
      if (response.error) {
        checkAndHandleAuth();
      }
      if (response.CHAT_HISTORY_ENABLED) {
        handleFetchHistory();
        setShowHistoryBtn(true);
      }
    })();
  }, []);

  const getMessagesByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.messages || [];
    }
    return [];
  };

  const getUseCaseByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.useCase || "";
    }
    return "";
  };

  const setMessagesByConvId = (id: string, messagesList: ChatMessage[]) => {
    const tempHistory = [...chatHistory];
    const matchedIndex = tempHistory.findIndex(
      (obj) => String(obj.id) === String(id),
    );
    if (matchedIndex > -1) {
      tempHistory[matchedIndex].messages = messagesList;
    }
  };

  const handleFetchHistory = async () => {
    if (fetchingChatHistory || !hasMoreRecords) {
      return;
    }
    setFetchingChatHistory(true);
    await historyList(offset).then((response) => {
      if (Array.isArray(response)) {
        setChatHistory((prevData) => [...prevData, ...response]);
        if (response.length === HISTORY_OFFSET_INCREMENT) {
          setOffset((offset) => (offset += HISTORY_OFFSET_INCREMENT));
          // Stopping offset increment if there were no records
        } else if (response.length < HISTORY_OFFSET_INCREMENT) {
          setHasMoreRecords(false);
        }
      } else {
        setChatHistory([]);
      }
      setFetchingChatHistory(false);
      return response;
    });
  };

  const onHistoryTitleChange = useCallback((id: string, newTitle: string) => {
    const tempChatHistory = [...chatHistory];
    const index = tempChatHistory.findIndex((obj) => obj.id === id);
    if (index > -1) {
      tempChatHistory[index].title = newTitle;
      setChatHistory(tempChatHistory);
    }
  }, [chatHistory]);

  const onHideClearAllDialog = () => {
    toggleClearAllDialog();
    setTimeout(() => {
      setClearingError(false);
    }, 2000);
  };

  return {
    chatHistory,
    clearing,
    clearingError,
    fetchingChatHistory,
    fetchingConvMessages,
    hideClearAllDialog,
    selectedConvId,
    showHistoryBtn,
    showHistoryPanel,
    toggleSpinner,
    getMessagesByConvId,
    getUseCaseByConvId,
    handleFetchHistory,
    onHideClearAllDialog,
    onHistoryTitleChange,
    setChatHistory,
    setClearing,
    setClearingError,
    setFetchingConvMessages,
    setMessagesByConvId,
    setSelectedConvId,
    setShowHistoryPanel,
    setToggleSpinner,
    toggleClearAllDialog,
  };
};
